import { Capacitor } from "@capacitor/core";
import axios, { type AxiosResponse, type InternalAxiosRequestConfig, isAxiosError } from "axios";
import { accessToken as token } from "../contexts/AuthProvider";
import { convertIsoDates } from "./dateUtils";
import { ClientType } from "./models";
import { isPublicCareersPath } from "./publicCareers";
export const baseURL = import.meta.env.VITE_SERVER_ADDRESS;

import { localStorageService } from "../services/LocalStorageService";
import { sessionStorageService } from "../services/SessionStorageService";
import { readRefreshToken } from "../services/storageService.service";
import { trackErrorEvent, trackEvent } from "./analytics";

// Create an Axios instance
const axiosInstance = axios.create({ baseURL });

let updatedToken: string;

let isDraftModeActive: boolean = false;

/**
 * The single in-flight `POST /refresh`, shared by every request that 401s while it runs.
 * Without it, an expired access token makes every concurrent request fire its own refresh
 * (a thundering herd that rotates the refresh-token cookie N times and row-locks the user).
 * Cleared in `finally` so the next expiry starts a fresh refresh.
 */
let refreshPromise: Promise<string> | null = null;

/**
 * Aborts the in-flight `POST /refresh` when its session ends. Paired with `refreshPromise`: the two
 * are installed and cleared together, so the controller always belongs to the promise in the slot.
 */
let refreshAbortController: AbortController | null = null;

/**
 * Bumped whenever a session ends, so a refresh started by the previous session can be recognised
 * as stale when it settles. Without this, clearing `refreshPromise` alone is not enough: an
 * already-in-flight refresh still resolves afterwards and would write the old session's token into
 * module state that the next session reads.
 */
let refreshEpoch = 0;

/**
 * The department this tab's access token opens, or null when the response did not confirm one. Kept
 * with the token so `useAdoptDepartment` can tell whether it must mint before showing a department.
 */
let tokenDepartmentId: string | null = null;

/**
 * Abandons any in-flight refresh so it cannot leak into the next session. Call on logout.
 *
 * The abort and the epoch bump cover different halves of the window and neither replaces the other:
 * aborting stops a request that has not come back yet — which also stops the *server* rotating a
 * refresh token for a session nobody will use — while the epoch catches one whose response already
 * landed and is between its `await` and its assignments, where there is nothing left to cancel.
 */
export const resetRefreshState = () => {
	refreshAbortController?.abort();
	refreshAbortController = null;
	refreshPromise = null;
	refreshEpoch += 1;
	tokenDepartmentId = null;
};

/** Sign-in pages: sending them to /login would loop. */
const LOGIN_FLOW_PATHS = ["/forgot-password", "/login", "/mfa", "/otp"];

/**
 * Pages a rejected session must not leave for /login. The public careers page is read by applicants who have no
 * account, often inside a customer's website: a stale InHouse cookie in their browser must never turn the job they
 * are reading into our sign-in screen.
 */
export const isLoginRedirectExempt = (pathname: string): boolean => LOGIN_FLOW_PATHS.includes(pathname) || isPublicCareersPath(pathname);

const DRAFT_MODE_WHITELIST = [
	"/fe-logs",
	"/shifts/draft-day-stats",
	"/shifts/predict-cost",
	"/shifts/predict-cost-batch",
	// Reads sent as POST only because their department list would overflow a URL — they write
	// nothing, so they're safe in draft mode.
	"/management_center/multi/ppd-stats",
	"/management_center/multi/hppd-stats",
	"/management_center/multi/cost-stats",
	"/management_center/multi/shift-pickups",
	"/management_center/multi/disciplinary-summary",
	"/management_center/multi/kpi-summary",
	"/management_center/multi/performance-over-time",
	"/management_center/multi/backoffice-grid",
];

/**
 * The endpoints that mint tokens. A 401 from one of these means the credential we would refresh
 * with was itself rejected, so refreshing again just repeats the rejection — and, because
 * `AuthProvider.refreshAccessToken` posts `/refresh` through this instance, would fire a duplicate
 * `/refresh` and race its own caller to a hard `/login` redirect, skipping that caller's teardown.
 *
 * This is the complete enumeration of every backend route under both prefixes, because a prefix
 * match is wrong in BOTH directions here and the two halves have to be checked separately:
 *
 * | route                      | credential it authenticates with | a 401 there means      | refresh? |
 * | -------------------------- | -------------------------------- | ---------------------- | -------- |
 * | `POST /token`              | email + password                 | bad credentials        | no       |
 * | `POST /token/sso/google`   | Google auth code                 | bad SSO credential     | no       |
 * | `POST /token/sso/apple`    | Apple identity token             | bad SSO credential     | no       |
 * | `POST /token/department`   | the **refresh token**            | refresh token rejected | no       |
 * | `POST /refresh`            | the refresh token                | refresh token rejected | no       |
 * | `POST /refresh/get-status-v2` | the **access token**          | access token expired   | **yes**  |
 *
 * So `/refresh*` needs an exact match (`get-status-v2` is a data-refresh poll that must keep
 * recovering from an expired token) while `/token*` would need a prefix one — and mixing the two
 * rules in a single predicate is how this gets silently wrong later. Listing the five paths costs a
 * line each and makes the next route under either prefix a deliberate decision rather than an
 * accident of string matching.
 *
 * `/token/department` is the one that reads as an exception and is not: it re-mints tokens from the
 * refresh token (`get_user_from_refresh_token`, `login.py:422`) and never looks at the access token,
 * so its 401 is the same rejection `/refresh` would get. The SSO pair matters for a second reason —
 * the retry-after-refresh would replay an already-consumed OAuth auth code, turning one failure into
 * Google's `invalid_grant`.
 */
const TOKEN_ENDPOINT_PATHS = ["/token", "/token/sso/google", "/token/sso/apple", "/token/department", "/refresh"];

/**
 * Reduces a request URL to the path the backend routes on, so the comparison above is like-for-like.
 * Every spelling that reaches the same route has to collapse to the same string here, because the
 * `/refresh` half of the list is an exact match and anything it fails to normalise silently falls
 * through to "refresh it" — the failure mode this guard exists to prevent.
 */
const requestPathOf = (url: string): string => {
	// Query and fragment: `/refresh?department_id=…` is the shape AuthProvider actually sends.
	const [pathWithOrigin] = url.split(/[?#]/);
	// Absolute URLs: axios accepts one even when `baseURL` is set, and it would never match a path.
	const withoutOrigin = pathWithOrigin.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, "");
	// Collapse repeated slashes — a `VITE_SERVER_ADDRESS` with a trailing slash yields `//refresh`,
	// the same seam `normalizeUrl` below guards on the outbound side.
	const collapsed = `/${withoutOrigin}`.replace(/\/{2,}/g, "/");
	// Drop a trailing slash: FastAPI 307-redirects `/refresh/` onto `/refresh`, so it reaches the
	// token endpoint and must be treated as one.
	return collapsed.length > 1 && collapsed.endsWith("/") ? collapsed.slice(0, -1) : collapsed;
};

const isTokenEndpoint = (url: string | undefined): boolean => (url ? TOKEN_ENDPOINT_PATHS.includes(requestPathOf(url)) : false);

/** The request-interceptor stamp that ties a request to the session that sent it. */
type SessionStampedConfig = InternalAxiosRequestConfig & { _sessionEpoch?: number };

/**
 * Rejects a request whose session ended before it could be retried. A plain `Error` rather than an
 * `AxiosError`, deliberately: the catch below reads a 401/403 on the refresh as "log out", and this
 * is not that — the session did not fail, it moved on, and the new one is healthy.
 */
const staleSessionError = () => new Error("request abandoned: the session changed before it could be retried");

/**
 * Whether `config` was sent by the session that is current right now. Every request through this
 * instance is stamped by the request interceptor, so an unstamped config means a hand-built one that
 * never crossed a session boundary and has nothing to compare.
 */
const belongsToCurrentSession = (config: SessionStampedConfig): boolean => config._sessionEpoch === undefined || config._sessionEpoch === refreshEpoch;

export const setDraftModeActive = (active: boolean) => {
	isDraftModeActive = active;
};

export const getTokenDepartmentId = (): string | null => tokenDepartmentId;

/** Moves on every token install and every logout, but not on a refresh, which renews the same session. */
export const getSessionEpoch = (): number => refreshEpoch;

/** Records the department the initial-load token was minted for — that path installs its token by hand. */
export const setTokenDepartmentId = (departmentId: string | null) => {
	tokenDepartmentId = departmentId;
};

export const updateAccessToken = (token: string, departmentId: string | null) => {
	// Installing a token IS a session boundary — login, MFA and the department switch all land here
	// without a logout. Any refresh started before this token existed is now stale by definition, so
	// it must not be able to overwrite what we are about to install. `performRefresh` captures its
	// epoch before its own await and writes these globals inline, so it cannot invalidate itself.
	resetRefreshState();

	updatedToken = token;
	tokenDepartmentId = departmentId;
	axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
};

/**
 * Current bearer token for non-axios transports (e.g. the Annie SSE fetch stream).
 * Same resolution order as the request interceptor below.
 */
export const getAccessToken = (): string | undefined => updatedToken || token;

// The refresh POST bypasses `axiosInstance` (and therefore its `baseURL`), so base and path are
// joined by hand here; trimming the seam guards against a `VITE_SERVER_ADDRESS` that ships a
// trailing slash, which would otherwise produce a `//refresh` the backend 404s on.
const normalizeUrl = (base: string, path: string): string => {
	const baseClean = base.endsWith("/") ? base.slice(0, -1) : base;
	const pathClean = path.startsWith("/") ? path : `/${path}`;
	return `${baseClean}${pathClean}`;
};

/**
 * Performs the token refresh and installs the new access token. Network + assignment work only —
 * no redirect decisions live here, because this promise is shared across concurrent 401s and each
 * waiter must make its own redirect decision against its own `window.location`.
 */
const performRefresh = async (nativeRefreshToken: string | undefined, signal: AbortSignal): Promise<string> => {
	// Captured before the await so a session teardown during the request is detectable on settle.
	const epoch = refreshEpoch;
	let response: AxiosResponse<{ access_token: string }>;
	// Renews the department this tab's token opens, not one read from storage: another tab may have
	// chosen a different department since, and renewing for it would leave this tab showing a department
	// its token doesn't open. A switch during the refresh installs its own token, which abandons this one.
	// DEFAULT: a token minted without a confirmed department renews for the department this tab chose.
	const department_id = tokenDepartmentId ?? localStorageService.getChosenDepartmentID();
	// Same as refreshAccessToken: re-send the view since the server can't recover it on refresh.
	const view = localStorageService.getChosenView();
	const params = new URLSearchParams();
	if (department_id) params.set("department_id", department_id);
	if (view) params.set("view", view);
	// Not params.size: browsers before Chrome 113 / Safari 17 don't have it, and the view would be dropped.
	const query = params.toString();
	const url = query ? `refresh?${query}` : "refresh";

	if (Capacitor.isNativePlatform()) {
		response = await axios.post(
			normalizeUrl(baseURL, url),
			{ refresh_token: nativeRefreshToken },
			{
				signal,
				headers: {
					"client-type": ClientType.MOBILE,
				},
			},
		);
	} else {
		response = await axios.post(normalizeUrl(baseURL, url), undefined, {
			signal,
			withCredentials: true,
			headers: {
				"client-type": ClientType.WEB,
			},
		});
	}
	const newAccessToken = response.data.access_token;

	// A 2xx carrying no usable token would otherwise install a nonsense credential globally and,
	// since one refresh now serves every waiter, break all of them at once. The response is typed
	// but never validated at runtime, so check the shape rather than mere truthiness: a non-string
	// would serialise into the header as `Bearer [object Object]`. Fail loud instead.
	if (typeof newAccessToken !== "string" || newAccessToken.length === 0) {
		trackEvent("401 refresh malformed response", { status: response.status });
		throw new Error("refresh response missing access_token");
	}

	// A refresh that outlived its session must not touch anything the next session reads. Rejecting
	// rather than resolving is deliberate: the retry below goes back through the request interceptor,
	// which sets Authorization from `updatedToken`, so a resolved waiter would replay its old-session
	// request under the NEW session's credentials. A plain Error (not an AxiosError) keeps the catch
	// from reading it as an auth rejection and redirecting to /login.
	if (epoch !== refreshEpoch) {
		throw new Error("refresh abandoned: the session changed while it was in flight");
	}

	updatedToken = newAccessToken;
	// The renewed token opens the department this refresh asked for.
	tokenDepartmentId = department_id;

	// Update the default header for future requests
	axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

	return newAccessToken;
};

axiosInstance.interceptors.request.use(
	(config) => {
		if (isDraftModeActive && config.method && config.method.toUpperCase() !== "GET") {
			const isWhitelisted = DRAFT_MODE_WHITELIST.some((route) => config.url?.includes(route));

			if (!isWhitelisted) {
				const error = new Error(`[Draft Mode] Blocked ${config.method.toUpperCase()} request to ${config.url}. Only GET requests are allowed in draft mode.`);
				error.name = "DraftModeBlockedRequest";
				trackErrorEvent("draft_mode_blocked_request", error, {
					method: config.method.toUpperCase(),
					url: config.url,
				});
				return Promise.reject(error);
			}
		}

		// A caller that set Authorization itself means it — re-impersonating has to send the SUPPORT
		// token, not the ambient one, which belongs to the impersonated user.
		const accessToken = updatedToken || token;
		if (accessToken && !config.headers.Authorization) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}

		// Stamp the session this request belongs to. A 401 can come back long after it was sent, by
		// which time the bearer above may have been replaced — and the response interceptor has to tell
		// *why*. A refresh leaves the epoch alone, a logout or a login moves it, so the pair (epoch,
		// bearer) is what separates "retry this with the newer token" from "this request's session is
		// gone, drop it". The cast carries a field axios has no slot for, like `_retry` below.
		(config as SessionStampedConfig)._sessionEpoch = refreshEpoch;

		// Set client-type header for all requests
		if (Capacitor.isNativePlatform()) {
			config.headers["client-type"] = ClientType.MOBILE;
			config.withCredentials = false;
		} else {
			config.headers["client-type"] = ClientType.WEB;
			config.withCredentials = true;
		}

		config.headers["X-Client-Session-Id"] = sessionStorageService.getOrCreateClientSessionId();

		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

axiosInstance.interceptors.response.use(
	(response: AxiosResponse): AxiosResponse => {
		// skip date conversion for binary responses
		if (response.config.responseType === "arraybuffer") {
			return response;
		}
		if (response.config.method === "get" && response.config.url === "/users/user_info") {
			return response;
		}

		response.data = convertIsoDates(response.data);
		return response;
	},
	async (error) => {
		const originalRequest = error.config;
		if (error.response?.status === 401 && !originalRequest._retry && !isTokenEndpoint(originalRequest.url)) {
			originalRequest._retry = true;
			try {
				// A 401 can arrive after its session is over — the response was in flight across a logout,
				// or across a login as someone else. Refreshing would mint a token nobody asked for, and
				// retrying would run this request as the new user, so neither is right: drop it.
				if (!belongsToCurrentSession(originalRequest)) {
					throw staleSessionError();
				}

				let nativeRefreshToken: string | undefined;
				if (Capacitor.isNativePlatform()) {
					// Reading the stored token stays PER-REQUEST and outside the shared promise: a missing
					// token rejects with *this* request's own error and deliberately never redirects, which
					// the shared refresh path (an explicit 401 → /login) must not be able to override.
					const { value: refreshToken, readFailed } = await readRefreshToken();
					if (!refreshToken) {
						trackEvent("401 refresh refresh_token unavailable", { reason: readFailed ? "read_failed" : "absent" });
						return Promise.reject(error);
					}
					nativeRefreshToken = refreshToken;

					// Re-checked because the keychain read is the one await between the check above and the
					// refresh below, and on device it is not instant. `performRefresh` captures its own epoch
					// only once called, so a session ending inside this window would otherwise look current
					// to it — and the refresh it started with the OLD session's stored token would install
					// over the token the NEW session had just put in place.
					if (!belongsToCurrentSession(originalRequest)) {
						throw staleSessionError();
					}
				}

				// The tail of the herd. Requests that went out together on one expired token do not 401
				// together; a straggler's 401 can land after the shared promise has already cleared, and
				// starting a second /refresh for it buys nothing — a healthy token is already installed.
				// Only the request is stale, not the session: that is what the epoch check above settled,
				// so replaying it under the current bearer is safe. Guarded on there being no refresh in
				// flight, because if there is one, joining it is still the right move.
				const currentToken = updatedToken || token;
				if (!refreshPromise && currentToken && originalRequest.headers?.Authorization && originalRequest.headers.Authorization !== `Bearer ${currentToken}`) {
					trackEvent("401 retried with an already-refreshed token");
					originalRequest.headers.Authorization = `Bearer ${currentToken}`;
					return axiosInstance(originalRequest);
				}

				if (!refreshPromise) {
					const controller = new AbortController();
					// `resetRefreshState()` can empty the slot while this refresh is still running, letting a
					// successor take it. Clearing unconditionally would then evict that successor and allow a
					// second concurrent /refresh — the very thing the shared promise exists to prevent. The
					// controller is cleared under the same identity check, so an abandoned refresh settling
					// late can never tear down the live one's only means of being cancelled.
					const inFlight: Promise<string> = performRefresh(nativeRefreshToken, controller.signal).finally(() => {
						if (refreshPromise === inFlight) {
							refreshPromise = null;
							refreshAbortController = null;
						}
					});
					refreshPromise = inFlight;
					refreshAbortController = controller;
				}
				const newAccessToken = await refreshPromise;

				// Update the authorization header of the original request
				originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

				// Retry the original request with the new token
				return axiosInstance(originalRequest);
			} catch (refreshError) {
				const { pathname } = window.location;
				const refreshStatus = isAxiosError(refreshError) ? refreshError.response?.status : undefined;
				const refreshRejected = refreshStatus === 401 || refreshStatus === 403;
				// Transient failures (network, 5xx, timeout) must keep the session intact
				// so the next request can succeed — only an explicit auth rejection logs out.
				if (refreshRejected && !isLoginRedirectExempt(pathname)) {
					window.location.href = "/login";
				}

				return Promise.reject(refreshError);
			}
		}
		return Promise.reject(error);
	},
);

export default axiosInstance;
